import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PageShell from "@/components/layout/PageShell";
import MasterTable from "@/components/MasterTable";
import { LayoutGrid, Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", company_id: "", active: true };

export default function MasterDepartmentsPage() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [d, c] = await Promise.all([
      api.get("/departments", { params: { company_id: companyFilter || undefined } }),
      api.get("/companies"),
    ]);
    setItems(d.data); setCompanies(c.data);
    // sensible default: first company if none picked
    setForm((f) => ({ ...f, company_id: f.company_id || (c.data[0]?.id || "") }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyFilter]);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const openCreate = () => { setForm({ ...EMPTY, company_id: companyFilter || companies[0]?.id || "" }); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ name: r.name, company_id: r.company_id || "", active: r.active !== false }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Department name is required");
    if (!form.company_id) return toast.error("Pick a company");
    try {
      if (editId) await api.patch(`/departments/${editId}`, form);
      else await api.post("/departments", form);
      toast.success(editId ? "Department updated" : "Department added");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Employees & cost centres in it will be unlinked.`)) return;
    try { await api.delete(`/departments/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const rows = useMemo(() => items.map((r) => ({ ...r, company_name: companyName(r.company_id) })), [items, companies]);

  return (
    <PageShell title="Departments" subtitle="Every employee & cost centre is pinned to (Company, Department) for billing" overline="Master · Departments">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <label className="flex items-center gap-2">
          <span className="overline">Filter by company</span>
          <select data-testid="dep-filter-company" value={companyFilter} onChange={(e)=>setCompanyFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">All</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <Button data-testid="dep-new" onClick={openCreate} className="rounded-md bg-primary text-primary-foreground">
          <Plus size={13} className="mr-1"/> Add department
        </Button>
      </div>
      <MasterTable
        columns={[
          { key: "name", label: "Department", render: (r) => (
              <div className="flex items-center gap-2"><LayoutGrid size={13} className="text-primary"/><span className="font-semibold">{r.name}</span></div>
          )},
          { key: "company_name", label: "Company", className: "text-xs text-muted-foreground" },
          { key: "active", label: "Status", render: (r) => (
              <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${
                r.active ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-400 text-slate-500 bg-slate-50"
              }`}>{r.active ? "Active" : "Inactive"}</span>
          )},
          { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
              <div className="inline-flex items-center gap-3">
                <button data-testid={`dep-edit-${r.id}`} onClick={()=>openEdit(r)} className="text-muted-foreground hover:text-primary"><Pencil size={13}/></button>
                <button data-testid={`dep-del-${r.id}`} onClick={()=>remove(r)} className="text-destructive hover:text-primary"><Trash2 size={13}/></button>
              </div>
          )},
        ]}
        rows={rows}
        searchKeys={["name", "company_name"]}
        getRowKey={(r) => r.id}
        testidPrefix="dep"
        defaultSort={{ key: "company_name", dir: "asc" }}
        emptyText="No departments yet."
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="dep-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit department" : "Add department"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <label className="block">
              <span className="overline">Company *</span>
              <select data-testid="dep-company" value={form.company_id} onChange={(e)=>setForm({...form, company_id:e.target.value})}
                      required className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm">
                <option value="">— pick a company —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="overline">Department name *</span>
              <Input data-testid="dep-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required className="mt-1 rounded-md" />
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form, active:e.target.checked})} data-testid="dep-active" className="h-4 w-4 accent-[#BE1E1E]"/>
              <span>Active</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)} className="rounded-md"><X size={13} className="mr-1"/>Cancel</Button>
              <Button type="submit" data-testid="dep-submit" className="rounded-md bg-primary text-primary-foreground">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
